/**
 * Formatter methods mixin
 *
 */

import i18n from '@/utils/i18n'
import Moment from 'moment/moment'
import AbstractApi from '@/api/abstractApi'
import number from '@/components/backoffice/type/number'
import appRouter from '@/router'
import { getFieldTypeTranslation} from '@/utils/fieldTypeCodes'

function areDatesFromSameDay (momentDate1, momentDate2) {
  if (momentDate1.date() !== momentDate2.date()) return false
  if (momentDate1.month() !== momentDate2.month()) return false
  return momentDate1.year() === momentDate2.year()
}

export default {
  methods: {
    formatDate (value) {
      if (value) {
        let date = new Moment(value)

        return i18n.d(date, 'date_short')
      }

      return ''
    },

    formatDateWithDay (value) {
      if (value) {
        let date = new Moment(value)

        return i18n.d(date, 'date_with_day')
      }

      return ''
    },

    formatDateTime (value, reformatIfToday = false) {
      if (value) {
        let date = new Moment(value)

        if (reformatIfToday) {
          let now = new Moment()

          if (areDatesFromSameDay(date, now)) {
            return i18n.d(date, 'time')
          }
        }

        return i18n.d(date, 'datetime_short')
      }

      return ''
    },

    formatDateTimeWithText (value, reformatIfToday = false) {
      let text = ''
      let val = ''

      if (value) {
        let date = new Moment(value)

        text = i18n.t('_until_day')
        val = i18n.d(date, 'datetime_short')

        if (reformatIfToday) {
          let now = new Moment()

          if (areDatesFromSameDay(date, now)) {
            text = i18n.t('_until_time')
            val = i18n.d(date, 'time')
          }
        }
      }

      return {
        text: text,
        value: val
      }
    },

    translateStatus (value) {
      return i18n.t(`booking.status.${value}.default`)
    },

    translateConst (value) {
      let ret

      switch (typeof value) {
        case 'object':
          let values = []

          for (let val of value) {
            values.push(i18n.t(val))
          }

          ret = values.join(' ')

          break
        case 'string':
          ret = i18n.t(value)
          break
      }

      return ret
    },

    formatPhoneNumber (value) {
      let ret = ''

      if (value) {
        value = value.replace(/\s/g, '')
        value = value.replace(/\./g, '')
        value = value.replace(/-/g, '')

        let end = value.slice(-8).replace(/(\d\d)(\d\d)(\d\d)(\d\d)/, '$1 $2 $3 $4')
        let start = value.slice(0, value.length - 8)

        ret = `${start} ${end}`
      }

      return ret
    },

    formatDecimal (value, nbDecimal = 1) {
      if (value) {
        value = parseFloat(value).toFixed(nbDecimal)
        value = value.replace('.', ',')
        return value
      }
      return ''
    },

    formatCurrency (value, currency = 'euro', nbDecimal = 2) {
      if (value) {
        let prefix = null
        let suffix = null

        let isEuro = ['eur', 'euro', 'EUR', 'EURO', '€'].indexOf(currency) !== -1

        value = parseFloat(value).toFixed(nbDecimal)

        if (isEuro) {
          suffix = ' €'
          value = value.replace('.', ',')
        }

        let ret = ''

        if (prefix) {
          ret = prefix
        }

        ret += value

        if (suffix) {
          ret += suffix
        }

        return ret
      }

      return ''
    },

    fullName (value, displayGender = true, isBbaContact = false) {
      let firstNameKeys = [
        'firstName',
        'first_name',
        'firstname' // BBA Contact
      ]

      let lastNameKeys = [
        'lastName',
        'last_name',
        'name' // BBA Contact
      ]

      let firstName = ''
      let lastName = ''

      if (isBbaContact && value.firstname === '') {
        let name = value.name.split(' ')
        firstName = name[0] + ' '
        lastName = name[1]
      } else {
        for (let key in value) {
          if (value.hasOwnProperty(key)) {
            if (firstNameKeys.indexOf(key) !== -1) {
              if (value[key]) firstName = this.formatToUCFirst(value[key]) + ' '
            }

            if (lastNameKeys.indexOf(key) !== -1) {
              if (value[key]) lastName = value[key].toUpperCase()
            }
          }
        }
      }

      let gender = displayGender ? this.formatGender(value.gender) + ' ' : ''

      return gender + firstName + lastName
    },

    formatHiddenText(value){
      let formattedValue = "";
      for (let i = 0;i<value.length;i++){
        if (value[i] === " ") {
          formattedValue+=" "
        }else{
          formattedValue += "*"
        }
      }
      return formattedValue;
    },

    formatDisplay (entity, entityName = null) {
      let display = ''

      if (entity) {
        if (!entityName) entityName = entity.classname
        let apiObject = new AbstractApi(entityName)

        if (entityName === 'user') {
          display = this.fullName(entity)
        } else {
          let displayFields = apiObject.getDisplayFields()

          if (displayFields) {
            let fieldsValues = []

            for (let field of displayFields) {
              fieldsValues.push(entity[field])
            }

            display = fieldsValues.join(' ')
          }
        }
      }

      return display
    },

    linkToShowSheet (entity, entityName = null) {
      // Ici, l'entité est celle retournée par le backoffice
      // L'entityName nous permet de retrouver la bonne configuration d'entité
      if (entity) {
        let display = this.formatDisplay(entity, entityName)
        let router = this.$router || appRouter
        let location = router.resolve({
          name: (entityName || entity.classname) + '_show',
          params: {
            id: entity.id
          }
        })

        return '<a href="' + location.href + '">' + display + '</a>'
      }

      return ''
    },

    formatCategory (nbStars) {
      let faClass = 'star'
      let ret = ''

      for (let i = 0; i < nbStars; i++) {
        ret += '<i class="fas fa-' + faClass + ' p-1"></i>'
      }

      return wrapWithDivField(ret, 'category')
    },

    formatArray (array) {
      let badgeClass = "badge-secondary";
      if(array.length > 0) badgeClass = "badge-success";
      return '<span class="badge badge-pill '+badgeClass+'">'+array.length+'</span>'
    },

    formatRole (roles) {
      let ret = ''

      for (let role of roles) {
        ret += '<span class="badge ' + role.toLowerCase() + '">' + this.translateConst(role) + '</span>'
      }

      return wrapWithDivField(ret, 'role')
    },

    formatBool (value, returnHtml = true) {
      let text = '_yes'
      let badgeClass = 'bg-success'
      let falseValues = ['false', 'f', 'no', 'non', '0']

      if (typeof value === 'number') {
        text = value > 0 ? 'OK' : 'Error'
        badgeClass = value > 0 ? 'bg-success' : 'bg-danger'
      } else {
        if (!value || falseValues.indexOf(value) !== -1) {
          text = '_no'
          badgeClass = 'bg-danger'
        }
      }

      if (returnHtml) {
        let ret = '<span class="badge ' + badgeClass + '">' + i18n.t(text) + '</span>'

        return wrapWithDivField(ret, 'boolean')
      } else {
        return text
      }
    },

    formatEmail (value) {
      return '<a href="mailto:' + value + '">' + value + '</a>'
    },

    formatUrl (value) {
      return '<a href="//' + value + '">' + value + '</a>'
    },

    formatNumber (value) {
      let props = number.props
      let badgeClass = value <= props.minThreshold.default ? 'bg-danger' : value >= props.maxThreshold.default ? 'bg-success' : 'bg-warning'

      let ret = '<span class="badge ' + badgeClass + '">' + value + '</span>'

      return wrapWithDivField(ret, 'number')
    },

    formatImage (value) {
      // TODO
      let ret = '<img src="' + value + '" />'

      return wrapWithDivField(ret, 'image')
    },

    translate (label, plural = false) {
      let global = '_' + label
      let specific = label += '.default'
      let amount = plural ? 2 : 1

      if (i18n.te(specific, i18n.fallbackLocale)) {
        return i18n.tc(specific, amount)
      } else {
        return i18n.tc(global, amount)
      }
    },

    formatGender (value) {
      let ret = ''

      if (value) {
        switch (value.toLowerCase()) {
          case 'm':
          case 'male':
          case 'mr':
          case 'mrs':
          case 'sir':
          case 'monsieur':
            ret = i18n.t('_gender_male_short')
            break
          case 'f':
          case 'female':
          case 'mme':
          case 'melle':
          case 'mlle':
          case 'miss':
          case 'madam':
            ret = i18n.t('_gender_female_short')
            break
        }
      }

      return ret
    },

    formatUserRole (isManager) {
      return isManager ? i18n.t('user.role_manager') : i18n.t('user.role_user')
    },

    formatToUCFirst (value) {
      if (value) {
        return value.charAt(0).toUpperCase() + value.slice(1)
      }
    },

    formatTolineFirst (value) {
      if (value) {
        //let first=value.substring(value.indexOf(','))
        let ret=value.split(',')
        let first=ret[0]
        return first
      }
    },

    formatTolineSecond (value) {
      if (value) {
        //let first=value.substring(value.indexOf(','))
        let ret=value.split(',')
        let second=ret[1]
        return second
      }
    },

    formatHotelName (hotel, displayCity = true) {
      let ret = hotel.name + ' ' + '*'.repeat(hotel.category)

      if (displayCity && hotel.city) ret += ' - ' + hotel.city.toUpperCase()

      return ret
    },

    formatFieldType (type) {
      return getFieldTypeTranslation(type)
    }
  }
}

function wrapWithDivField (inner, type) {
  return '<div class="field ' + type + '">' + inner + '</div>'
}
